import * as signalR from "@microsoft/signalr"
import auth from "../authConfig.js"

class SignalRService {
  constructor() {
    this.connection = null
    this.conditionsConnection = null
    this.repriceConnection = null

    this._startingMessageHub = null
    this._startingConditionsHub = null
    this._startingRepriceHub = null

    this._recentLoansAzureId = null

    this._messageHubClosedHandlers = []
    this._messageHubReconnectingHandlers = []
    this._messageHubReconnectedHandlers = []

    this._conditionsHubClosedHandlers = []
    this._conditionsHubReconnectingHandlers = []
    this._conditionsHubReconnectedHandlers = []

    this._repriceHubClosedHandlers = []
    this._repriceHubReconnectingHandlers = []
    this._repriceHubReconnectedHandlers = []

    this.lockExtensionConnection = null
    this._startingLockExtensionHub = null

    this._lockExtensionHubClosedHandlers = []
    this._lockExtensionHubReconnectingHandlers = []
    this._lockExtensionHubReconnectedHandlers = []

    // Secondary cross-environment hub connections (Option A): the SPA also listens to the
    // OTHER environment's reprice/lock-extension hubs so it receives processed events
    // regardless of which API instance the processing engine notified. The DB is shared,
    // so a reload triggered by either hub surfaces the change.
    this.repriceConnectionSecondary = null
    this.lockExtensionConnectionSecondary = null
  }

  // Normalized secondary API base (no trailing slash), or "" if unset/same as primary.
  _secondaryApiBase() {
    const norm = (u) => (u && u.endsWith("/") ? u.slice(0, -1) : (u || ""))
    const primary = norm(process.env.VUE_APP_API_ENDPOINT || "")
    const secondary = norm(process.env.VUE_APP_API_ENDPOINT_SECONDARY || "")
    return secondary && secondary !== primary ? secondary : ""
  }

  // Build + start a hub connection at `url`, wiring its reconnect to the shared handler list.
  async _startSecondaryHub(url, reconnectedHandlers) {
    const conn = this.buildConditionsConnection(url)
    conn.onclose(err => console.log("SignalR: secondary hub closed", url, err))
    conn.onreconnecting(err => console.log("SignalR: secondary hub reconnecting", url, err))
    conn.onreconnected(id => this._safeInvokeHandlers(reconnectedHandlers, id))
    if (conn.state === signalR.HubConnectionState.Disconnected) {
      await conn.start()
      console.log("SignalR: secondary hub connected", url)
    }
    return conn
  }

  _repriceConns() {
    return [this.repriceConnection, this.repriceConnectionSecondary].filter(Boolean)
  }

  _lockExtensionConns() {
    return [this.lockExtensionConnection, this.lockExtensionConnectionSecondary].filter(Boolean)
  }

  async acquireToken() {
    try {
      const token = await auth.acquireTokenResponse()
      return token?.accessToken || ""
    } catch {
      return ""
    }
  }

  buildConnection(url) {
    return new signalR.HubConnectionBuilder()
      .withUrl(url)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
      .configureLogging(signalR.LogLevel.Information)
      .build()
  }

  buildConditionsConnection(url) {
    return new signalR.HubConnectionBuilder()
      .withUrl(url, {
        accessTokenFactory: () => this.acquireToken()
      })
      .withAutomaticReconnect([0, 2000, 5000, 10000, 20000])
      .configureLogging(signalR.LogLevel.Information)
      .build()
  }

  _safeInvokeHandlers(list, arg) {
    if (!Array.isArray(list) || list.length === 0) return
    list.forEach(fn => {
      try {
        fn(arg)
      } catch (err) {
        console.error("SignalR: handler error", err)
      }
    })
  }

  onMessageHubClosed(callback) {
    if (typeof callback !== "function") return
    this._messageHubClosedHandlers.push(callback)
  }

  onMessageHubReconnecting(callback) {
    if (typeof callback !== "function") return
    this._messageHubReconnectingHandlers.push(callback)
  }

  onMessageHubReconnected(callback) {
    if (typeof callback !== "function") return
    this._messageHubReconnectedHandlers.push(callback)
  }

  onConditionsClosed(callback) {
    if (typeof callback !== "function") return
    this._conditionsHubClosedHandlers.push(callback)
  }

  onConditionsReconnecting(callback) {
    if (typeof callback !== "function") return
    this._conditionsHubReconnectingHandlers.push(callback)
  }

  onConditionsReconnected(callback) {
    if (typeof callback !== "function") return
    this._conditionsHubReconnectedHandlers.push(callback)
  }

  onRepriceHubClosed(callback) {
    if (typeof callback !== "function") return
    this._repriceHubClosedHandlers.push(callback)
  }

  onRepriceHubReconnecting(callback) {
    if (typeof callback !== "function") return
    this._repriceHubReconnectingHandlers.push(callback)
  }

  onRepriceHubReconnected(callback) {
    if (typeof callback !== "function") return
    this._repriceHubReconnectedHandlers.push(callback)
  }

  async startConnection(azureId = null) {
    if (this.connection?.state === signalR.HubConnectionState.Connected) {
      if (azureId) {
        this._recentLoansAzureId = azureId
        await this.joinRecentLoansGroup(azureId)
      }
      return
    }

    if (this._startingMessageHub) return this._startingMessageHub

    this._startingMessageHub = (async () => {
      const swiftApiEndpoint = process.env.VUE_APP_SWIFT_API_ENDPOINT || ""
      const base = swiftApiEndpoint.endsWith("/") ? swiftApiEndpoint.slice(0, -1) : swiftApiEndpoint
      const url = `${base}/messageservice`

      if (!this.connection) {
        this.connection = this.buildConnection(url)

        this.connection.onclose(error => {
          console.log("SignalR: MessageHub closed", error)
          this._safeInvokeHandlers(this._messageHubClosedHandlers, error)
        })

        this.connection.onreconnecting(error => {
          console.log("SignalR: MessageHub reconnecting...", error)
          this._safeInvokeHandlers(this._messageHubReconnectingHandlers, error)
        })

        this.connection.onreconnected(async connectionId => {
          console.log("SignalR: MessageHub reconnected", connectionId)
          this._safeInvokeHandlers(this._messageHubReconnectedHandlers, connectionId)

          if (this._recentLoansAzureId) {
            try {
              await this.joinRecentLoansGroup(this._recentLoansAzureId)
            } catch (err) {
              console.error("SignalR: failed to rejoin RecentLoansGroup", err)
            }
          }
        })
      }

      if (this.connection.state === signalR.HubConnectionState.Disconnected) {
        await this.connection.start()
        console.log("SignalR: MessageHub connected")
      }

      if (azureId) {
        this._recentLoansAzureId = azureId
        await this.joinRecentLoansGroup(azureId)
      }
    })().finally(() => {
      this._startingMessageHub = null
    })

    return this._startingMessageHub
  }

  async startConditionsConnection() {
    if (this.conditionsConnection?.state === signalR.HubConnectionState.Connected) return
    if (this._startingConditionsHub) return this._startingConditionsHub

    this._startingConditionsHub = (async () => {
      const apiEndpoint = process.env.VUE_APP_API_ENDPOINT || ""
      const base = apiEndpoint.endsWith("/") ? apiEndpoint.slice(0, -1) : apiEndpoint
      const url = `${base}/hubs/conditions`

      if (!this.conditionsConnection) {
        this.conditionsConnection = this.buildConditionsConnection(url)

        this.conditionsConnection.onclose(error => {
          console.log("SignalR: ConditionsHub closed", error)
          this._safeInvokeHandlers(this._conditionsHubClosedHandlers, error)
        })

        this.conditionsConnection.onreconnecting(error => {
          console.log("SignalR: ConditionsHub reconnecting...", error)
          this._safeInvokeHandlers(this._conditionsHubReconnectingHandlers, error)
        })

        this.conditionsConnection.onreconnected(connectionId => {
          console.log("SignalR: ConditionsHub reconnected", connectionId)
          this._safeInvokeHandlers(this._conditionsHubReconnectedHandlers, connectionId)
        })
      }

      if (this.conditionsConnection.state === signalR.HubConnectionState.Disconnected) {
        await this.conditionsConnection.start()
        console.log("SignalR: ConditionsHub connected")
      }
    })().finally(() => {
      this._startingConditionsHub = null
    })

    return this._startingConditionsHub
  }

  async joinRecentLoansGroup(azureId) {
    if (!this.connection) return
    if (this.connection.state !== signalR.HubConnectionState.Connected) return
    await this.connection.invoke("JoinRecentLoansGroup", azureId)
    this._recentLoansAzureId = azureId
  }

  async leaveRecentLoansGroup(azureId) {
    if (!this.connection) return
    if (this.connection.state !== signalR.HubConnectionState.Connected) return
    await this.connection.invoke("LeaveRecentLoansGroup", azureId)
    if (this._recentLoansAzureId === azureId) {
      this._recentLoansAzureId = null
    }
  }

  onBorrowerTasksUpdated(callback) {
    if (!this.connection) return
    this.connection.off("BorrowerTasksUpdated")
    this.connection.on("BorrowerTasksUpdated", callback)
  }

  onPipelineManagementUpdated(callback) {
    if (!this.connection) return
    this.connection.off("PipelineManagementUpdated")
    this.connection.on("PipelineManagementUpdated", callback)
  }

  onLoanTasksUpdated(callback) {
    if (!this.connection) return
    this.connection.off("LoanTasksUpdated")
    this.connection.on("LoanTasksUpdated", callback)
  }

  onRecentLoanUpdated(callback) {
    if (!this.connection) return
    this.connection.off("RecentLoanUpdated")
    this.connection.on("RecentLoanUpdated", callback)
  }

  onNeedsAttentionChanged(callback) {
    if (!this.conditionsConnection) return
    this.conditionsConnection.off("NeedsAttentionChanged")
    this.conditionsConnection.on("NeedsAttentionChanged", callback)
  }

  onNoteSubmitted(callback) {
    if (!this.conditionsConnection) return
    this.conditionsConnection.off("NoteSubmitted")
    this.conditionsConnection.on("NoteSubmitted", callback)
  }

  async stopConnection() {
    if (!this.connection) return
    if (this.connection.state === signalR.HubConnectionState.Disconnected) return
    await this.connection.stop()
    this._recentLoansAzureId = null
  }

  async stopConditionsConnection() {
    if (!this.conditionsConnection) return
    if (this.conditionsConnection.state === signalR.HubConnectionState.Disconnected) return
    await this.conditionsConnection.stop()
  }

  isConnectionActive() {
    return !!this.connection && this.connection.state === signalR.HubConnectionState.Connected
  }

  isConditionsConnectionActive() {
    return !!this.conditionsConnection && this.conditionsConnection.state === signalR.HubConnectionState.Connected
  }

  async startRepriceConnection() {
    if (this.repriceConnection?.state === signalR.HubConnectionState.Connected) return
    if (this._startingRepriceHub) return this._startingRepriceHub

    this._startingRepriceHub = (async () => {
      const apiEndpoint = process.env.VUE_APP_API_ENDPOINT || ""
      const base = apiEndpoint.endsWith("/") ? apiEndpoint.slice(0, -1) : apiEndpoint
      const url = `${base}/hubs/reprice`

      if (!this.repriceConnection) {
        this.repriceConnection = this.buildConditionsConnection(url) // reuses auth token factory

        this.repriceConnection.onclose(error => {
          console.log("SignalR: RepriceHub closed", error)
          this._safeInvokeHandlers(this._repriceHubClosedHandlers, error)
        })

        this.repriceConnection.onreconnecting(error => {
          console.log("SignalR: RepriceHub reconnecting...", error)
          this._safeInvokeHandlers(this._repriceHubReconnectingHandlers, error)
        })

        this.repriceConnection.onreconnected(connectionId => {
          console.log("SignalR: RepriceHub reconnected", connectionId)
          this._safeInvokeHandlers(this._repriceHubReconnectedHandlers, connectionId)
        })
      }

      if (this.repriceConnection.state === signalR.HubConnectionState.Disconnected) {
        await this.repriceConnection.start()
        console.log("SignalR: RepriceHub connected")
      }

      // Also connect to the OTHER environment's reprice hub, if configured.
      const secondaryBase = this._secondaryApiBase()
      if (secondaryBase && !this.repriceConnectionSecondary) {
        try {
          this.repriceConnectionSecondary = await this._startSecondaryHub(
            `${secondaryBase}/hubs/reprice`, this._repriceHubReconnectedHandlers)
        } catch (e) {
          console.log("SignalR: secondary RepriceHub failed (non-fatal)", e)
        }
      }
    })().finally(() => {
      this._startingRepriceHub = null
    })

    return this._startingRepriceHub
  }

  /**
   * Register a callback fired whenever the reprice engine marks a queue item processed.
   * Payload: { queueId, loanId, decision, processedAt }. Attached to BOTH primary and
   * secondary (cross-environment) hubs so the event is received either way.
   */
  onQueueItemProcessed(callback) {
    this._repriceConns().forEach(c => c.on("QueueItemProcessed", callback))
  }

  offQueueItemProcessed(callback) {
    this._repriceConns().forEach(c => c.off("QueueItemProcessed", callback))
  }

  async stopRepriceConnection() {
    for (const c of this._repriceConns()) {
      if (c.state !== signalR.HubConnectionState.Disconnected) await c.stop()
    }
  }

  isRepriceConnectionActive() {
    return this._repriceConns().some(c => c.state === signalR.HubConnectionState.Connected)
  }

  onLockExtensionHubClosed(callback) {
    if (typeof callback !== "function") return
    this._lockExtensionHubClosedHandlers.push(callback)
  }

  onLockExtensionHubReconnecting(callback) {
    if (typeof callback !== "function") return
    this._lockExtensionHubReconnectingHandlers.push(callback)
  }

  onLockExtensionHubReconnected(callback) {
    if (typeof callback !== "function") return
    this._lockExtensionHubReconnectedHandlers.push(callback)
  }

  async startLockExtensionConnection() {
    if (this.lockExtensionConnection?.state === signalR.HubConnectionState.Connected) return
    if (this._startingLockExtensionHub) return this._startingLockExtensionHub

    this._startingLockExtensionHub = (async () => {
      const apiEndpoint = process.env.VUE_APP_API_ENDPOINT || ""
      const base = apiEndpoint.endsWith("/") ? apiEndpoint.slice(0, -1) : apiEndpoint
      const url = `${base}/hubs/lock-extensions`

      if (!this.lockExtensionConnection) {
        this.lockExtensionConnection = this.buildConditionsConnection(url)

        this.lockExtensionConnection.onclose(error => {
          console.log("SignalR: LockExtensionHub closed", error)
          this._safeInvokeHandlers(this._lockExtensionHubClosedHandlers, error)
        })

        this.lockExtensionConnection.onreconnecting(error => {
          console.log("SignalR: LockExtensionHub reconnecting...", error)
          this._safeInvokeHandlers(this._lockExtensionHubReconnectingHandlers, error)
        })

        this.lockExtensionConnection.onreconnected(connectionId => {
          console.log("SignalR: LockExtensionHub reconnected", connectionId)
          this._safeInvokeHandlers(this._lockExtensionHubReconnectedHandlers, connectionId)
        })
      }

      if (this.lockExtensionConnection.state === signalR.HubConnectionState.Disconnected) {
        await this.lockExtensionConnection.start()
        console.log("SignalR: LockExtensionHub connected")
      }

      // Also connect to the OTHER environment's lock-extension hub, if configured.
      const secondaryBase = this._secondaryApiBase()
      if (secondaryBase && !this.lockExtensionConnectionSecondary) {
        try {
          this.lockExtensionConnectionSecondary = await this._startSecondaryHub(
            `${secondaryBase}/hubs/lock-extensions`, this._lockExtensionHubReconnectedHandlers)
        } catch (e) {
          console.log("SignalR: secondary LockExtensionHub failed (non-fatal)", e)
        }
      }
    })().finally(() => {
      this._startingLockExtensionHub = null
    })

    return this._startingLockExtensionHub
  }

  onLockExtensionQueueItemProcessed(callback) {
    this._lockExtensionConns().forEach(c => c.on("QueueItemProcessed", callback))
  }

  offLockExtensionQueueItemProcessed(callback) {
    this._lockExtensionConns().forEach(c => c.off("QueueItemProcessed", callback))
  }

  onLockExtensionManualStatusUpdated(callback) {
    this._lockExtensionConns().forEach(c => c.on("ManualStatusUpdated", callback))
  }

  offLockExtensionManualStatusUpdated(callback) {
    this._lockExtensionConns().forEach(c => c.off("ManualStatusUpdated", callback))
  }

  onLockExtensionRecommendationRejected(callback) {
    this._lockExtensionConns().forEach(c => c.on("RecommendationRejected", callback))
  }

  offLockExtensionRecommendationRejected(callback) {
    this._lockExtensionConns().forEach(c => c.off("RecommendationRejected", callback))
  }

  onLockExtensionApprovedRequestSubmitted(callback) {
    this._lockExtensionConns().forEach(c => c.on("ApprovedRequestSubmitted", callback))
  }

  offLockExtensionApprovedRequestSubmitted(callback) {
    this._lockExtensionConns().forEach(c => c.off("ApprovedRequestSubmitted", callback))
  }

  // Fired whenever the lock desk queue itself is mutated (status change, assignment,
  // creation/intake) by any user — payload: { itemId, action }. Used to live-refresh the queue.
  onLockDeskQueueChanged(callback) {
    this._lockExtensionConns().forEach(c => c.on("LockDeskQueueChanged", callback))
  }

  offLockDeskQueueChanged(callback) {
    this._lockExtensionConns().forEach(c => c.off("LockDeskQueueChanged", callback))
  }

  async stopLockExtensionConnection() {
    for (const c of this._lockExtensionConns()) {
      if (c.state !== signalR.HubConnectionState.Disconnected) await c.stop()
    }
  }

  isLockExtensionConnectionActive() {
    return this._lockExtensionConns().some(c => c.state === signalR.HubConnectionState.Connected)
  }
}

export default new SignalRService()
